import * as Sentry from '@sentry/nextjs';
import { toast } from '@appgnostic/design-system';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

import { resolveMutationError } from '@/src/client/error-handling/resolve-mutation-error';
import { translateMutationError } from '@/src/client/error-handling/translate-mutation-error';

/**
 * Options for configuring the query error handler
 */
export interface UseQueryErrorHandlerOptions {
  /**
   * Whether to show a toast notification for the error.
   * @default true
   */
  showToast?: boolean;

  /**
   * Whether to log the error to Sentry.
   * @default true
   */
  logToSentry?: boolean;

  /**
   * Custom default translation key if the error code is not recognized.
   * If not provided, uses 'generic.unexpectedError'
   */
  defaultErrorKey?: string;

  /**
   * Additional tags to add to Sentry error logging.
   * Note: 'layer: react-query' is always added automatically.
   */
  sentryTags?: Record<string, string>;

  /**
   * Additional context to add to Sentry error logging.
   */
  sentryContext?: Record<string, unknown>;
}

/**
 * Result returned by the error handler, including the translated message
 * and metadata for custom handling.
 */
export interface QueryErrorHandlerResult {
  /**
   * The translated user-friendly error message
   */
  message: string;

  /**
   * The error code from ApiClientError (if available)
   */
  code?: string;

  /**
   * HTTP status code (if available)
   */
  status?: number;
}

/**
 * Hook that provides standardized error handling for React Query mutations.
 *
 * This hook:
 * - Translates errors to user-friendly messages using i18n
 * - Shows toast notifications (configurable)
 * - Logs errors to Sentry with appropriate tags (configurable)
 * - Returns error metadata for custom handling
 *
 * **Use this in `onError` callbacks for React Query mutations** to ensure
 * consistent error UX and observability across the application.
 *
 * @param options - Configuration options for error handling behavior
 * @returns Function to handle errors in React Query mutation callbacks
 *
 * @example Basic usage
 * ```tsx
 * function MyComponent() {
 *   const handleError = useQueryErrorHandler();
 *
 *   const mutation = useMutation({
 *     mutationFn: createWidget,
 *     onError: (error) => {
 *       handleError(error);
 *     }
 *   });
 * }
 * ```
 *
 * @example With custom options
 * ```tsx
 * function MyComponent() {
 *   const handleError = useQueryErrorHandler({
 *     defaultErrorKey: 'widget.operationFailed',
 *     sentryTags: { feature: 'widgets' }
 *   });
 *
 *   const mutation = useMutation({
 *     mutationFn: createWidget,
 *     onError: handleError
 *   });
 * }
 * ```
 *
 * @example With custom post-processing
 * ```tsx
 * function MyComponent() {
 *   const handleError = useQueryErrorHandler();
 *
 *   const mutation = useMutation({
 *     mutationFn: createWidget,
 *     onError: (error) => {
 *       const result = handleError(error);
 *       // Custom handling based on error code
 *       if (result.code === 'RATE_LIMITED') {
 *         setShowRateLimitBanner(true);
 *       }
 *     }
 *   });
 * }
 * ```
 *
 * @example Disable toast or Sentry for specific scenarios
 * ```tsx
 * const handleError = useQueryErrorHandler({
 *   showToast: false, // Handle UI display manually
 *   logToSentry: false // Don't log expected errors
 * });
 * ```
 */
export function useQueryErrorHandler(
  options: UseQueryErrorHandlerOptions = {}
): (error: unknown) => QueryErrorHandlerResult {
  const {
    showToast = true,
    logToSentry = true,
    defaultErrorKey,
    sentryTags = {},
    sentryContext = {},
  } = options;

  const t = useTranslations('errors');

  return useCallback(
    (error: unknown): QueryErrorHandlerResult => {
      // Translate error to user-friendly message
      const translatedMessage = translateMutationError(
        error,
        t,
        defaultErrorKey ? { defaultKey: defaultErrorKey } : {}
      );

      // Resolve error metadata (code, status, etc.)
      const resolution = resolveMutationError(
        error,
        defaultErrorKey ? { defaultKey: defaultErrorKey } : {}
      );

      // Show toast notification if enabled
      if (showToast) {
        toast.error(translatedMessage);
      }

      // Log to Sentry if enabled
      if (logToSentry) {
        Sentry.captureException(error, {
          tags: {
            layer: 'react-query',
            errorCode: resolution.code ?? 'unknown',
            ...sentryTags,
          },
          extra: {
            translatedMessage,
            errorStatus: resolution.status,
            errorCode: resolution.code,
            translationKey: resolution.descriptor.key,
            ...sentryContext,
          },
        });
      }

      // Return result for custom handling
      return {
        message: translatedMessage,
        ...(resolution.code && { code: resolution.code }),
        ...(resolution.status && { status: resolution.status }),
      };
    },
    [t, showToast, logToSentry, defaultErrorKey, sentryTags, sentryContext]
  );
}
